/*
  `_cw_ctx` -- the one hidden line property that records WHERE on the store a
  cart line was added and HOW. Attribution push A (P3-lite):
  docs/plans/boops-attribution-implementation-plan-2026-09-28.md §4 + §7, on top
  of docs/plans/boops-attribution-foundation-design-2026-09-28.md §3.1.

      _cw_ctx = v1;s=<surface_id>;m=<mechanism>[;c=<card_id>]

  `surface_id` is `<page_kind>.<page_key>.<slot>` ([a-z0-9_-] parts, <= 64
  chars), `mechanism` is one of the storefront values in
  `data-warehouse/dbt/seeds/seed_add_mechanisms.csv`, and `card_id` is the
  buy-box card. There is no `o=`: the warehouse resolves the offer from the
  selling plan, `_cw_route` and the timestamp (plan §4).

  Rules this file owns (foundation §3.1):
    S-1  the stamp rides the add request that already exists -- never a second
         cart write. This file performs NO network request at all.
    S-2  never empty, no personal data: `stamp()` returns null for anything
         outside the grammar and the caller then sends no property.
    S-5  first add owns the line's stamp: `adoptLineStamp()` makes a new add of
         a variant + plan that is already in the cart carry that line's own
         `_cw_ctx` (or none), so Shopify merges the add instead of splitting it.
         A split subscription line becomes a SECOND Recharge subscription
         (audit A2 D-7), so this is a correctness rule, not an analytics nicety.
    S-7  the Functions never read this key (CI: check_cw_ctx_guards.py).

  BB-01 (docs/plans/boops-bb01-cutover-implementation-plan-2026-09-29.md §4 B):
  on a pinned product page the PDP form also sends `_cw_route` (the route
  marker, server-rendered by snippets/buy-buttons.liquid). While the form
  carries one, S-5 treats it like the stamp for a line with NO route or the
  SAME route: a repeat add of that variant + plan takes the line's `_cw_ctx`
  and `_cw_route` (or none), so it merges and pays that line's price, which is
  never more than the page showed. A line priced by ANOTHER route (a buy-box
  line) is never joined: its `max_units` could leave the added unit at full
  price, so the add stays a separate line, exactly as an unpinned PDP add does
  today. A one-time add drops the marker. A form with no marker (every
  unpinned page) runs exactly the rule above.

  Pricing is untouched: `_cw_route` stays the only line key either Function
  reads. The PDP form path does not need this file to stamp (its stamp is a
  server-rendered hidden input in snippets/buy-buttons.liquid, which survives
  the no-JS fallback and IAB script failures); it needs it only for S-5.

  `parse()` is the storefront twin of the warehouse parser and exists for the
  shared grammar fixture (shopify/offers/cw-ctx-grammar-fixture.json). Unknown
  versions and malformed values are labelled `unknown`, never repaired.

  One global, `window.__cwCtx` (registry row:
  docs/research/receipts/boops-fs-buybox/cw-globals-registry-2026-09-15.md).
  No build step, no imports; the same file is `require`d by the node suites.
*/
(function (globalScope) {
  "use strict";

  var KEY = "_cw_ctx";
  var VERSION = "v1";
  var FIELD = "properties[" + KEY + "]";
  var ROUTE_KEY = "_cw_route";
  var ROUTE_FIELD = "properties[" + ROUTE_KEY + "]";
  // The price Function's marker grammar (cart_lines_discounts_generate_run.js).
  var ROUTE = /^bb-v[0-9]{1,3}-c[0-9]{1,4}$/;
  var SURFACE_MAX = 64;
  var SURFACE = /^[a-z0-9_-]+\.[a-z0-9_-]+\.[a-z0-9_-]+$/;
  var MECHANISM = /^[a-z_]{1,32}$/;
  var CARD = /^[a-z0-9_-]{1,64}$/;
  var VERSION_TOKEN = /^v[0-9]{1,3}$/;
  var FIELD_KEY = /^[a-z]{1,16}$/;
  // The mechanisms a storefront client may WRITE: the `storefront` group of
  // seed_add_mechanisms minus the warehouse-derived `permalink_add` and
  // `unmarked_web`. A typo at a call site yields no stamp rather than a value
  // the registry has never heard of.
  var CLIENT_MECHANISMS = [
    "atc_main", "quick_add", "bb_subscribe", "bb_one_time",
    "drawer_upsell", "drawer_empty", "ugc_carousel", "gift_auto"
  ];

  function isSurface(value) {
    return typeof value === "string" && value.length <= SURFACE_MAX && SURFACE.test(value);
  }

  // The value for one line, or null when any part is outside the grammar.
  function stamp(surface, mechanism, card) {
    if (!isSurface(surface)) return null;
    if (CLIENT_MECHANISMS.indexOf(mechanism) < 0) return null;
    var value = VERSION + ";s=" + surface + ";m=" + mechanism;
    if (card === undefined || card === null || card === "") return value;
    if (typeof card !== "string" || !CARD.test(card)) return null;
    return value + ";c=" + card;
  }

  // The `data-cw-surface` projection: the nearest server-rendered surface id
  // at or above `element`. Null when the page declares none.
  function surfaceOf(element) {
    var node = element;
    while (node && typeof node.getAttribute === "function") {
      var value = node.getAttribute("data-cw-surface");
      if (value !== null) return isSurface(value) ? value : null;
      node = node.parentNode;
    }
    return null;
  }

  function unknown(version) {
    return { v: version, s: null, m: null, c: null, confidence: "unknown" };
  }

  // Grammar v1, forward compatible (plan §4): an unknown version keeps its
  // token and nothing else; a v1 value is key-based, so new keys are additive
  // and ignored; anything malformed is `unknown` with every part null.
  function parse(raw) {
    if (typeof raw !== "string" || raw === "") return unknown(null);
    var tokens = raw.split(";");
    if (!VERSION_TOKEN.test(tokens[0])) return unknown(null);
    var version = tokens[0];
    if (version !== VERSION) return unknown(version);
    var fields = {};
    for (var index = 1; index < tokens.length; index += 1) {
      var token = tokens[index];
      var cut = token.indexOf("=");
      if (cut < 1) return unknown(version);
      var key = token.slice(0, cut);
      var value = token.slice(cut + 1);
      if (!FIELD_KEY.test(key) || value === "" ||
          Object.prototype.hasOwnProperty.call(fields, key)) {
        return unknown(version);
      }
      fields[key] = value;
    }
    if (!isSurface(fields.s) || !MECHANISM.test(fields.m || "") ||
        (fields.c !== undefined && !CARD.test(fields.c))) {
      return unknown(version);
    }
    return {
      v: version,
      s: fields.s,
      m: fields.m,
      c: fields.c === undefined ? null : fields.c,
      confidence: "stamped"
    };
  }

  function planOf(line) {
    var allocation = line && line.selling_plan_allocation;
    var plan = allocation && allocation.selling_plan;
    return plan && plan.id !== undefined && plan.id !== null ? String(plan.id) : "";
  }

  // Every OTHER line property the form would send (all but the `skip` keys).
  // Shopify merges an add into an existing line only when variant, plan AND the
  // full property set match, so S-5 may only borrow a stamp from a line the add
  // would have merged into anyway had neither side carried one.
  function formProperties(formData, skip) {
    var properties = {};
    formData.forEach(function (value, name) {
      if (typeof value !== "string") return;
      var match = /^properties\[(.+)\]$/.exec(name);
      if (!match || skip.indexOf(match[1]) >= 0) return;
      properties[match[1]] = value;
    });
    return properties;
  }

  function sameOtherProperties(lineProperties, formProps, skip) {
    var line = {};
    Object.keys(lineProperties || {}).forEach(function (key) {
      if (skip.indexOf(key) < 0) line[key] = lineProperties[key];
    });
    var lineKeys = Object.keys(line).sort();
    var formKeys = Object.keys(formProps).sort();
    if (lineKeys.length !== formKeys.length) return false;
    return lineKeys.every(function (key, index) {
      return key === formKeys[index] && String(line[key]) === formProps[key];
    });
  }

  // The server-rendered stamp that buy-buttons.liquid shipped `disabled`
  // because the cart already held an unstamped line of this product (the
  // no-JS half of S-5). Only a value the grammar accepts is ever restored.
  function deferredStamp(form) {
    if (!form || typeof form.querySelector !== "function") return null;
    var input = form.querySelector('input[name="' + FIELD + '"][data-cw-ctx-deferred]');
    var value = input && typeof input.value === "string" ? input.value : "";
    return value && parse(value).confidence === "stamped" ? value : null;
  }

  // The server-rendered BB-01 marker that buy-buttons.liquid shipped `disabled`
  // (the no-JS half of S-5 for `_cw_route`). Only the Function's grammar passes.
  function deferredRoute(form) {
    if (!form || typeof form.querySelector !== "function") return null;
    var input = form.querySelector('input[name="' + ROUTE_FIELD + '"][data-cw-route-deferred]');
    var value = input && typeof input.value === "string" ? input.value : "";
    return ROUTE.test(value) ? value : null;
  }

  // The add joins `properties`' line: it takes that line's stamp and marker
  // (or none of either), so Shopify merges it and the line keeps its price.
  function adoptLine(formData, properties) {
    var changed = false;
    var carried = false;
    [[FIELD, KEY], [ROUTE_FIELD, ROUTE_KEY]].forEach(function (pair) {
      var value = properties && properties[pair[1]];
      if (typeof value === "string" && value !== "") {
        carried = true;
        if (formData.get(pair[0]) !== value) {
          formData.set(pair[0], value);
          changed = true;
        }
      } else if (formData.has(pair[0])) {
        formData.delete(pair[0]);
        changed = true;
      }
    });
    if (!changed) return "kept";
    return carried ? "adopted" : "removed";
  }

  // S-5 on the cart the theme already holds (ProductForm awaits `fetchCart`
  // before it posts), so it costs no request. Returns what it did, for tests:
  //   "adopted"   the form now carries the existing line's stamp
  //   "removed"   the mergeable line has no stamp, so the form sends none
  //   "kept"      the form's stamp already equals the line's
  //   "restored"  no mergeable line, so the deferred server stamp is sent
  //   "none"      no mergeable line; the form is left exactly as it was
  // With no cart (the baseline read failed) nothing changes: a deferred stamp
  // stays off, which can lose a stamp but can never split a line.
  //
  // BB-01: a form that carries a route marker (enabled or deferred) is "routed".
  // A routed ONE-TIME add drops the marker first (it belongs on a subscription
  // line). A routed subscription add compares lines without `_cw_ctx` AND
  // `_cw_route`; a mergeable line with no route or this route gives the add
  // both of its values, a line on another route is skipped. With no mergeable
  // line, a deferred marker is restored like a deferred stamp.
  function adoptLineStamp(formData, cart, form) {
    if (!formData || typeof formData.get !== "function" ||
        typeof formData.forEach !== "function") return "none";
    var plan = String(formData.get("selling_plan") || "");
    var routed = formData.has(ROUTE_FIELD) || deferredRoute(form) !== null;
    if (routed && !plan) {
      formData.delete(ROUTE_FIELD);
      routed = false;
    }
    if (!cart || !Array.isArray(cart.items)) return "none";
    var variant = String(formData.get("id") || "");
    if (!variant) return "none";
    var skip = routed ? [KEY, ROUTE_KEY] : [KEY];
    var marker = routed ? String(formData.get(ROUTE_FIELD) || deferredRoute(form) || "") : "";
    var others = formProperties(formData, skip);
    for (var index = 0; index < cart.items.length; index += 1) {
      var line = cart.items[index] || {};
      if (String(line.variant_id || "") !== variant || planOf(line) !== plan) continue;
      if (!sameOtherProperties(line.properties, others, skip)) continue;
      if (routed) {
        var lineRoute = line.properties && line.properties[ROUTE_KEY];
        if (typeof lineRoute === "string" && lineRoute !== "" && lineRoute !== marker) continue;
        return adoptLine(formData, line.properties);
      }
      var existing = line.properties && line.properties[KEY];
      if (typeof existing === "string" && existing !== "") {
        if (formData.get(FIELD) === existing) return "kept";
        formData.set(FIELD, existing);
        return "adopted";
      }
      if (formData.has(FIELD)) formData.delete(FIELD);
      return "removed";
    }
    var restored = false;
    var deferred = deferredStamp(form);
    if (deferred && !formData.has(FIELD)) {
      formData.set(FIELD, deferred);
      restored = true;
    }
    var route = routed ? deferredRoute(form) : null;
    if (route && !formData.has(ROUTE_FIELD)) {
      formData.set(ROUTE_FIELD, route);
      restored = true;
    }
    return restored ? "restored" : "none";
  }

  var api = {
    KEY: KEY,
    VERSION: VERSION,
    MECHANISMS: CLIENT_MECHANISMS.slice(),
    stamp: stamp,
    surfaceOf: surfaceOf,
    parse: parse,
    adoptLineStamp: adoptLineStamp
  };

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  } else if (globalScope && !globalScope.__cwCtx) {
    globalScope.__cwCtx = api;
  }
}(typeof globalThis === "object" ? globalThis : this));
